import moment from "moment";
import { destroyCookie, parseCookies, setCookie } from "nookies";
import { basePath } from "../../config";

export const DEV_STATE =
  process.env.NEXT_PUBLIC_STATE === `development` ? true : false;
export const SERVERSIDE_HOST = process.env
  .NEXT_PUBLIC_SERVERSIDE_HOST as string;
export const VALLARIS_HOST = process.env.NEXT_PUBLIC_HOST as string;
export const KEY_TOKEN = process.env.NEXT_PUBLIC_COOKIE_KEY_TOKEN as string;

export const REFRESH_TOKEN = process.env
  .NEXT_PUBLIC_COOKIE_KEY_REFRESH_TOKEN as string;

export const RELEASE = process.env.NEXT_PUBLIC_RELEASE as string;

export const CORE_API = process.env.NEXT_PUBLIC_API_URL;

export interface Iapi {
  accessToken?: string | undefined;
  controller?: AbortController;
  apikey?: string | undefined;
  swr?: boolean;
  cacheId?: string;
  refreshToken?: string;
}

export interface IFilterBasic {
  search?: string;
  limit?: number;
  offset?: number;
  numberMatched?: number;
  numberReturned?: number;
  sortby?: string;
  type?: any;
}

export const pathUrlFunction = (
  type:
    | "management"
    | "fixed"
    | "style"
    | "portal"
    | "analytics"
    | "tile"
    | "feature"
    | "server"
    | "process",
  path: string,
  urlOrigin?: string | undefined
) => {
  let api = undefined;
  if (type === "management") {
    api = `${
      urlOrigin ? urlOrigin : VALLARIS_HOST
    }/${CORE_API}/managements/1.0`;
  } else if (type === "style") {
    api = `${
      urlOrigin ? urlOrigin : VALLARIS_HOST
    }/${CORE_API}/styles/1.0-beta/styles`;
  } else if (type === "portal") {
    api = `${urlOrigin ? urlOrigin : VALLARIS_HOST}/${CORE_API}/portal/1.1`;
  } else if (type === "analytics") {
    api = `${urlOrigin ? urlOrigin : VALLARIS_HOST}/${CORE_API}/analytics/1.0`;
  } else if (type === "tile") {
    api = `${
      urlOrigin ? urlOrigin : VALLARIS_HOST
    }/${CORE_API}/tiles/1.0-beta/tiles`;
  } else if (type === "feature") {
    api = `${
      urlOrigin ? urlOrigin : VALLARIS_HOST
    }/${CORE_API}/features/1.0/collections`;
  } else if (type === "server") {
    api = `${urlOrigin ? urlOrigin : ``}${basePath}/api`;
  } else if (type === "fixed") {
    api = ``;
  } else if (type === "process") {
    api = `${urlOrigin ? urlOrigin : VALLARIS_HOST}/${CORE_API}/processes`;
  }

  return `${api}${path}`;
};

export const getOptionAPI = (
  method: "GET" | "PUT" | "PATCH" | "DELETE" | "POST",
  authorization: boolean,
  optionAPI?: Iapi,
  body?: any
) => {
  let option: any;

  option = {
    method: method,
    headers: {
      "Content-Type": "application/json",
    },
  };

  if (authorization) {
    const allCookie = parseCookies(null);

    const token =
      optionAPI?.accessToken !== undefined
        ? optionAPI.accessToken
        : allCookie[KEY_TOKEN];

    if (optionAPI?.apikey) {
      option.headers["API-Key"] = optionAPI.apikey;
    } else {
      option.headers["Authorization"] = `Bearer ${token ?? "token"}`;
    }
  }

  if (optionAPI?.controller?.signal) {
    option.signal = optionAPI.controller.signal;
  }

  if (body) {
    option.body = JSON.stringify(body);
  }

  return option;
};

export const postRefreshToken = (
  refreshToken: string,
  urlOrigin?: string | undefined
) => {
  const pathUrl = pathUrlFunction("management", `/token`, urlOrigin);

  const urlencoded = new URLSearchParams();
  urlencoded.append("grant_type", "refresh_token");
  urlencoded.append("refresh_token", refreshToken);
  const options = {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: urlencoded,
  };

  return fetch(pathUrl, options)
    .then(async (response) => {
      try {
        return { status: response.status, result: await response.json() };
      } catch (error) {
        return { status: response.status, result: null };
      }
    })
    .then((result) => {
      return result;
    })
    .catch((error: TypeError) => {
      return { status: 500, result: error };
    });
};

export const refreshFunction = async (
  pathUrl: string,
  option: any,
  refreshToken?: string
) => {
  const allCookie = parseCookies(null);

  const rsRefreshToken: any = await postRefreshToken(
    refreshToken ? refreshToken : allCookie[REFRESH_TOKEN as any]
  );

  if (rsRefreshToken.status === 200) {
    setCookie(null, REFRESH_TOKEN, rsRefreshToken.result.refreshToken, {
      path: "/",
      expires: moment().add(7, "days").toDate(),
      sameSite: "strict",
    });
    setCookie(null, KEY_TOKEN, rsRefreshToken.result.accessToken, {
      path: "/",
      expires: moment().add(1, "hours").toDate(),
      sameSite: "strict",
    });

    return fetch(pathUrl, {
      ...option,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${rsRefreshToken.result.accessToken}`,
      },
    })
      .then(async (response) => {
        try {
          return response.status === 401
            ? { status: response.status }
            : { status: response.status, result: await response.json() };
        } catch (error) {
          return { status: response.status };
        }
      })
      .then((rs) => {
        return {
          ...rs,
          accessToken: rsRefreshToken.result.accessToken,
        };
      })
      .catch((_error) => ({ status: 401 }));
  } else {
    destroyCookie(null, KEY_TOKEN as any, {
      path: "/",
    });
    destroyCookie(null, REFRESH_TOKEN as any, {
      path: "/",
    });

    throw { title: "Cannot refresh token" };
  }
};

export const fetcher = (
  path: string,
  option: any,
  accessToken?: string,
  refreshToken?: string
) => {
  return fetch(path, option)
    .then(async (response) => {
      try {
        return { status: response.status, result: await response.json() };
      } catch (error) {
        return { status: response.status };
      }
    })
    .then(async (result) => {
      if (result.status === 401) {
        const RefreshToken: any = await refreshFunction(
          path,
          option,
          refreshToken
        );
        return RefreshToken;
      } else {
        return {
          ...result,
          accessToken: accessToken,
        };
      }
    })
    .catch((error: TypeError) => {
      throw error;
    });
};

export const parseBody = (arg: { body?: any; id?: string }, option: any) => {
  return arg?.body
    ? { ...option, body: JSON.stringify(arg.body) }
    : { ...option };
};

export interface IArg {
  arg: { body?: any; id?: string };
}

export interface IuseFetch {
  data: any;
  isLoading: boolean;
  isError: boolean;
  mutate: () => void;
  isValidating: boolean;
}
